import api from "../lib/api";

export interface BookingGuest {
  id: number;
  guest_type: "AD" | "CH";
  first_name: string;
  surname: string;
  age?: number;
  passport_number?: string;
  nationality?: string;
}

export interface Booking {
  id: number;
  booking_reference: string;
  booking_id?: string;
  client_reference?: string;
  supplier_reference?: string;
  hotel_id: number;
  hotel_name: string;
  city: string;
  country: string;
  street_address?: string;
  hotel_phone?: string;
  checkin_time?: string;
  checkout_time?: string;
  room_plan_id: number;
  plan_name: string;
  plan_code: string;
  meal_plan?: string;
  room_type_name: string;
  room_description?: string;
  checkin_date: string;
  checkout_date: string;
  nights_count?: number;
  guest_title?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  guest_name: string;
  guest_email: string;
  guest_phone?: string;
  guest_nationality?: string;
  guest_address?: string;
  adults: number;
  adults_count?: number;
  children: number;
  children_count?: number;
  total_guests?: number;
  rooms: number;
  rooms_count?: number;
  base_amount?: number;
  markup_amount?: number;
  service_charge_amount?: number;
  total_amount: number;
  pending_amount?: number;
  currency: string;
  is_refundable?: boolean;
  cancellation_policy?: string;
  cancellation_deadline?: string;
  original_cancellation_policy?: {
    amount: string;
    deadline: string;
    text: string;
  };
  current_cancellation_status?: "refundable" | "non_refundable" | "unknown";
  current_cancellation_message?: string;
  time_remaining_hours?: number;
  special_requests?: string;
  internal_remarks?: string;
  booking_source: "TRP World" | "Direct" | "OTA" | "Corporate" | "Walk-in";
  booking_channel?: string;
  booking_status:
    | "confirmed"
    | "booked (HCN Pending)"
    | "pending (HBR Pending)"
    | "cancelled"
    | "no_show"
    | "checked_in"
    | "checked_out";
  payment_status: "pending" | "paid" | "failed" | "refunded";
  booking_datetime?: string;
  confirmation_datetime?: string;
  cancellation_datetime?: string;
  cancellation_reason?: string;
  cancellation_charge?: number;
  refund_amount?: number;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  hotel_booking_ref_number?: string | null;
  hotel_confirmation_number?: string | null;
  guests?: BookingGuest[];
  notes?: string;
}

export interface BookingFilters {
  hotel_id?: number;
  booking_status?: string;
  payment_status?: string;
  booking_source?: string;
  booking_id?: string;
  booking_date_from?: string;
  booking_date_to?: string;
  checkin_date_from?: string;
  checkin_date_to?: string;
  checkout_date_from?: string;
  checkout_date_to?: string;
  guest_email?: string;
  guest_name?: string;
  booking_reference?: string;
  hotel_booking_ref_number?: string;
  hotel_confirmation_number?: string;
  hotel_name?: string;
  hotel_city?: string;
  hotel_country?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface BookingResponse {
  success: boolean;
  data: Booking[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

export interface BookingAuditLog {
  id: number;
  booking_id: number;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: number;
  changed_by_name: string;
  change_reason: string | null;
  created_at: string;
}

export interface UpdateBookingRequest {
  booking_status?: string;
  payment_status?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  guest_email?: string;
  guest_phone?: string;
  guest_nationality?: string;
  guest_address?: string;
  checkin_date?: string;
  checkout_date?: string;
  room_plan_id?: number;
  base_amount?: number;
  markup_amount?: number;
  service_charge_amount?: number;
  total_amount?: number;
  notes?: string;
  hotel_booking_ref_number?: string | null;
  hotel_confirmation_number?: string | null;
  change_reason: string;
}

class BookingService {
  async getBookings(
    page: number = 1,
    limit: number = 10,
    filters: BookingFilters = {}
  ): Promise<BookingResponse> {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (filters.hotel_id)
      params.append("hotel_id", filters.hotel_id.toString());
    if (filters.booking_status)
      params.append("booking_status", filters.booking_status);
    if (filters.payment_status)
      params.append("payment_status", filters.payment_status);
    if (filters.booking_source)
      params.append("booking_source", filters.booking_source);
    if (filters.booking_id) params.append("booking_id", filters.booking_id);
    if (filters.booking_date_from)
      params.append("booking_date_from", filters.booking_date_from);
    if (filters.booking_date_to)
      params.append("booking_date_to", filters.booking_date_to);
    if (filters.checkin_date_from)
      params.append("checkin_date_from", filters.checkin_date_from);
    if (filters.checkin_date_to)
      params.append("checkin_date_to", filters.checkin_date_to);
    if (filters.checkout_date_from)
      params.append("checkout_date_from", filters.checkout_date_from);
    if (filters.checkout_date_to)
      params.append("checkout_date_to", filters.checkout_date_to);
    if (filters.guest_email) params.append("guest_email", filters.guest_email);
    if (filters.guest_name) params.append("guest_name", filters.guest_name);
    if (filters.booking_reference)
      params.append("booking_reference", filters.booking_reference);
    if (filters.hotel_booking_ref_number)
      params.append(
        "hotel_booking_ref_number",
        filters.hotel_booking_ref_number
      );
    if (filters.hotel_confirmation_number)
      params.append(
        "hotel_confirmation_number",
        filters.hotel_confirmation_number
      );
    if (filters.hotel_name) params.append("hotel_name", filters.hotel_name);
    if (filters.hotel_city) params.append("hotel_city", filters.hotel_city);
    if (filters.hotel_country)
      params.append("hotel_country", filters.hotel_country);
    if (filters.sort_by) params.append("sort_by", filters.sort_by);
    if (filters.sort_order) params.append("sort_order", filters.sort_order);

    const response = await api.get(`/bookings?${params}`);
    return response.data;
  }

  async getBookingById(
    id: number
  ): Promise<{ success: boolean; data: Booking }> {
    const response = await api.get(`/bookings/${id}`);
    return response.data;
  }

  async updateBooking(
    id: number,
    updateData: UpdateBookingRequest
  ): Promise<{ success: boolean; message: string; changes: number }> {
    const response = await api.put(`/bookings/${id}`, updateData);
    return response.data;
  }

  async getBookingAuditLogs(
    id: number
  ): Promise<{ success: boolean; data: BookingAuditLog[] }> {
    const response = await api.get(`/bookings/${id}/audit-logs`);
    return response.data;
  }
}

export default new BookingService();
